/**
 * DeadlineOverview - Deadline grouped list view
 * Stitch design system
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Collapse, Tooltip, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

const T = {
  primary: '#526075', primaryContainer: '#d5e3fd',
  tertiary: '#635983',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', error: '#a83836', errorBg: '#fef2f2',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

// Drie momenten waarop een PL iets moet doen. Start en eind komen van een taak,
// de deadline hoort bij een deliverable (het moment richting klant).
const momentConfig = {
  start: { label: 'Start', icon: 'play_arrow', color: '#3b82f6', bg: '#eff6ff', hint: 'Iemand begint hieraan — jouw input nodig' },
  eind: { label: 'Oplevering', icon: 'task_alt', color: '#10b981', bg: '#ecfdf5', hint: 'Taak klaar — checken en doorsturen naar de klant' },
  deadline: { label: 'Deliverable', icon: 'movie', color: '#8b5cf6', bg: '#f5f3ff', hint: 'Deadline richting klant' },
};

const deadlineTypeColors = {
  'Hard': { bg: '#fee2e2', text: '#dc2626' },
  'Klant': { bg: '#ffedd5', text: '#ea580c' },
  'Zacht': { bg: '#ccfbf1', text: '#0d9488' },
  'Intern': { bg: '#dcfce7', text: '#16a34a' },
};

const getDateGroup = (daysUntil) => { if (daysUntil < 0) return 'overdue'; if (daysUntil === 0) return 'today'; if (daysUntil <= 7) return 'thisWeek'; if (daysUntil <= 14) return 'nextWeek'; return 'later'; };

const dateGroupConfig = {
  overdue: { label: 'Verlopen', color: '#dc2626', bg: '#fef2f2' },
  today: { label: 'Vandaag', color: '#f59e0b', bg: '#fffbeb' },
  thisWeek: { label: 'Deze week', color: '#3b82f6', bg: '#eff6ff' },
  nextWeek: { label: 'Volgende week', color: '#8b5cf6', bg: '#f5f3ff' },
  later: { label: 'Later', color: T.outline, bg: T.surfaceLow },
};

const formatDate = (dateStr) => { if (!dateStr) return ''; return new Date(dateStr).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }); };
const getDaysLabel = (d) => { if (d < 0) return `${Math.abs(d)}d over`; if (d === 0) return 'Vandaag'; if (d === 1) return 'Morgen'; return `${d}d`; };

/* ── Deadline row ──────────────────────────────────────────────────────────── */
function DeadlineRow({ item, showProject = true, onClick }) {
  const tc = deadlineTypeColors[item.deadlineType] || deadlineTypeColors.Intern;
  const mc = momentConfig[item.moment] || momentConfig.deadline;
  const isOverdue = item.daysUntil < 0;
  const isUrgent = item.daysUntil >= 0 && item.daysUntil <= 3;

  return (
    <Box onClick={onClick} sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, px: 1.5,
      cursor: 'pointer', transition: 'background 0.12s',
      '&:hover': { bgcolor: 'rgba(45,51,53,0.02)' },
      borderBottom: `1px solid ${T.surfaceLow}`,
      '&:last-child': { borderBottom: 'none' },
    }}>
      <Tooltip title={mc.hint} arrow>
        <Box sx={{ display: 'flex', flexShrink: 0 }}>
          <Icon name={mc.icon} size={14} sx={{ color: mc.color }} />
        </Box>
      </Tooltip>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 500, color: T.text }}>{item.name}</Typography>
        {item.assignee && (
          <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', color: T.outlineVariant }}>{item.assignee}</Typography>
        )}
      </Box>
      {showProject && <Typography noWrap sx={{ width: 130, fontFamily: T.fontBody, fontSize: '0.65rem', color: T.outlineVariant, flexShrink: 0 }}>{item.projectName}</Typography>}
      <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 700, color: mc.color, bgcolor: mc.bg, px: 0.75, py: 0.15, borderRadius: '9999px', flexShrink: 0 }}>{mc.label}</Typography>
      {item.type === 'deliverable' && (
        <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 600, color: tc.text, bgcolor: tc.bg, px: 0.75, py: 0.15, borderRadius: '9999px', flexShrink: 0 }}>{item.deadlineType}</Typography>
      )}
      <Typography sx={{ width: 48, fontFamily: T.fontBody, fontSize: '0.65rem', color: T.outline, textAlign: 'right', flexShrink: 0 }}>{formatDate(item.deadline)}</Typography>
      <Typography component="span" sx={{
        fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 700, minWidth: 50, textAlign: 'center',
        color: isOverdue ? '#dc2626' : isUrgent ? '#b45309' : T.outline,
        bgcolor: isOverdue ? '#fee2e2' : isUrgent ? '#fef3c7' : T.surfaceLow,
        px: 0.75, py: 0.15, borderRadius: '9999px',
      }}>{getDaysLabel(item.daysUntil)}</Typography>
    </Box>
  );
}

/* ── Date group ────────────────────────────────────────────────────────────── */
function DateGroup({ groupKey, items, onItemClick }) {
  const c = dateGroupConfig[groupKey];
  if (!items || items.length === 0) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.6, bgcolor: c.bg, borderRadius: '0.5rem 0.5rem 0 0' }}>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700, color: c.color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{c.label}</Typography>
        <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.5rem', fontWeight: 700, color: '#fff', bgcolor: c.color, px: 0.6, py: 0.1, borderRadius: '9999px' }}>{items.length}</Typography>
      </Box>
      <Box sx={{ bgcolor: T.surface, border: `1px solid ${T.surfaceLow}`, borderTop: 'none', borderRadius: '0 0 0.5rem 0.5rem' }}>
        {items.map(item => <DeadlineRow key={item.id} item={item} onClick={() => onItemClick(item)} />)}
      </Box>
    </Box>
  );
}

/* ── Project group ─────────────────────────────────────────────────────────── */
function ProjectGroup({ project, items, expanded, onToggle, onItemClick }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Box onClick={onToggle} sx={{
        display: 'flex', alignItems: 'center', gap: 1, p: 1.25,
        bgcolor: expanded ? T.surfaceLow : T.surface, borderRadius: expanded ? '0.5rem 0.5rem 0 0' : '0.5rem',
        boxShadow: T.shadow, cursor: 'pointer', transition: 'background 0.12s',
        '&:hover': { bgcolor: T.surfaceLow },
      }}>
        <Icon name={expanded ? 'expand_less' : 'expand_more'} size={16} sx={{ color: T.outline }} />
        <Icon name="folder" size={14} sx={{ color: T.outlineVariant }} />
        <Typography noWrap sx={{ flex: 1, fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 700, color: T.text }}>{project.projectName}</Typography>
        <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 600, color: T.outline, bgcolor: T.surfaceLow, px: 1, py: 0.2, borderRadius: '9999px' }}>
          {items.length} {items.length === 1 ? 'deadline' : 'deadlines'}
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ bgcolor: T.surface, border: `1px solid ${T.surfaceLow}`, borderTop: 'none', borderRadius: '0 0 0.5rem 0.5rem' }}>
          {items.map(item => <DeadlineRow key={item.id} item={item} showProject={false} onClick={() => onItemClick(item)} />)}
        </Box>
      </Collapse>
    </Box>
  );
}

/* ── Skeleton ──────────────────────────────────────────────────────────────── */
function DeadlinesSkeleton() {
  return (
    <Box>
      {[1, 2, 3].map(g => (
        <Box key={g} sx={{ mb: 2 }}>
          <Skeleton variant="rounded" height={28} sx={{ borderRadius: '0.5rem 0.5rem 0 0' }} />
          <Box sx={{ border: `1px solid ${T.surfaceLow}`, borderTop: 'none', borderRadius: '0 0 0.5rem 0.5rem' }}>
            {[1, 2, 3].map(r => (
              <Box key={r} sx={{ display: 'flex', gap: 1.5, p: 1.25 }}>
                <Skeleton variant="circular" width={16} height={16} />
                <Skeleton variant="text" width="40%" height={16} />
                <Skeleton variant="text" width="20%" height={16} />
                <Skeleton variant="rounded" width={45} height={16} />
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/* ── Main ──────────────────────────────────────────────────────────────────── */
function DeadlineOverview({ userEmail }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [deadlines, setDeadlines] = useState({ byDate: [], byProject: {} });
  const [isCached, setIsCached] = useState(false);
  const [groupMode, setGroupMode] = useState('date');
  const [momentFilter, setMomentFilter] = useState('all');
  const [expandedProjects, setExpandedProjects] = useState({});

  const fetchDeadlines = async (forceRefresh = false) => {
    if (!userEmail) return;
    if (forceRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/my-deadlines?email=${encodeURIComponent(userEmail)}&includeStart=true${forceRefresh ? '&refresh=true' : ''}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setDeadlines(d);
      setIsCached(d.cached === true);
      const exp = {};
      Object.keys(d.byProject || {}).forEach(k => { exp[k] = true; });
      setExpandedProjects(exp);
    } catch (err) { setError(`Kon deadlines niet laden: ${err.message}`); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchDeadlines(); }, [userEmail]);

  const visibleItems = useMemo(() => (
    (deadlines.byDate || []).filter(i => momentFilter === 'all' || (i.moment || 'deadline') === momentFilter)
  ), [deadlines.byDate, momentFilter]);

  const momentCounts = useMemo(() => {
    const c = { all: 0, start: 0, eind: 0, deadline: 0 };
    (deadlines.byDate || []).forEach(i => { c.all += 1; c[i.moment || 'deadline'] = (c[i.moment || 'deadline'] || 0) + 1; });
    return c;
  }, [deadlines.byDate]);

  const visibleByProject = useMemo(() => {
    const acc = {};
    visibleItems.forEach(item => {
      const pid = item.projectId || 'unknown';
      if (!acc[pid]) acc[pid] = { projectId: pid, projectName: item.projectName, projectNumber: item.projectNumber, items: [] };
      acc[pid].items.push(item);
    });
    return acc;
  }, [visibleItems]);

  const itemsByDateGroup = useMemo(() => {
    const groups = { overdue: [], today: [], thisWeek: [], nextWeek: [], later: [] };
    visibleItems.forEach(item => { groups[getDateGroup(item.daysUntil)].push(item); });
    return groups;
  }, [visibleItems]);

  const handleItemClick = (item) => { if (item.projectId) navigate(`/projects/${item.projectId}`); };
  const toggleProject = (id) => { setExpandedProjects(prev => ({ ...prev, [id]: !prev[id] })); };

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', bgcolor: T.errorBg, borderRadius: '0.75rem', border: `1px solid ${T.error}20` }}>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.error }}>{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {[
            { key: 'date', label: 'Op deadline', icon: 'calendar_today' },
            { key: 'project', label: 'Per project', icon: 'folder' },
          ].map(opt => (
            <Box key={opt.key} onClick={() => setGroupMode(opt.key)} sx={{
              px: 1.5, py: 0.5, borderRadius: '9999px', cursor: 'pointer',
              bgcolor: groupMode === opt.key ? T.primaryContainer : T.surfaceLow,
              border: `1.5px solid ${groupMode === opt.key ? `${T.primary}40` : 'transparent'}`,
              display: 'flex', alignItems: 'center', gap: 0.5, transition: 'all 0.15s',
              '&:hover': groupMode !== opt.key ? { bgcolor: T.surfaceHigh } : {},
            }}>
              <Icon name={opt.icon} size={14} sx={{ color: groupMode === opt.key ? T.primary : T.outline }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: groupMode === opt.key ? 700 : 500, color: groupMode === opt.key ? T.primary : T.outline }}>
                {opt.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {[
            { key: 'all', label: 'Alles' },
            { key: 'start', label: 'Start' },
            { key: 'eind', label: 'Oplevering' },
            { key: 'deadline', label: 'Deliverable' },
          ].map(opt => (
            <Box key={opt.key} onClick={() => setMomentFilter(opt.key)} sx={{
              px: 1.25, py: 0.4, borderRadius: '9999px', cursor: 'pointer',
              bgcolor: momentFilter === opt.key ? (momentConfig[opt.key]?.bg || T.primaryContainer) : T.surfaceLow,
              border: `1.5px solid ${momentFilter === opt.key ? `${momentConfig[opt.key]?.color || T.primary}55` : 'transparent'}`,
              display: 'flex', alignItems: 'center', gap: 0.4, transition: 'all 0.15s',
              '&:hover': momentFilter !== opt.key ? { bgcolor: T.surfaceHigh } : {},
            }}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: momentFilter === opt.key ? 700 : 500, color: momentFilter === opt.key ? (momentConfig[opt.key]?.color || T.primary) : T.outline }}>
                {opt.label}
              </Typography>
              <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700, color: T.outlineVariant }}>
                {momentCounts[opt.key] || 0}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isCached && (
            <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 600, color: T.outlineVariant, bgcolor: T.surfaceLow, px: 0.75, py: 0.15, borderRadius: '9999px' }}>cached</Typography>
          )}
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline }}>{visibleItems.length} items</Typography>
          <Tooltip title="Ververs data" arrow
            slotProps={{ tooltip: { sx: { fontFamily: T.fontBody, fontSize: '0.72rem', bgcolor: T.text, borderRadius: '0.5rem', px: 1.5, py: 0.75 } }, arrow: { sx: { color: T.text, '&::before': { border: 'none' } } } }}
          >
            <Box onClick={() => !refreshing && fetchDeadlines(true)} sx={{
              width: 28, height: 28, borderRadius: '0.35rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: refreshing ? 'default' : 'pointer', transition: 'all 0.15s',
              '&:hover': !refreshing ? { bgcolor: T.surfaceLow } : {},
            }}>
              {refreshing ? (
                <Box sx={{ width: 14, height: 14, border: `2px solid ${T.outlineVariant}`, borderTop: `2px solid ${T.tertiary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
              ) : (
                <Icon name="refresh" size={16} sx={{ color: T.outline }} />
              )}
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {loading && <DeadlinesSkeleton />}

      {!loading && (!deadlines.byDate || deadlines.byDate.length === 0) && (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Icon name="event_busy" size={48} sx={{ color: T.outlineVariant, display: 'block', margin: '0 auto 12px' }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.92rem', fontWeight: 600, color: T.outline, mb: 0.5 }}>Geen deadlines gevonden</Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outlineVariant }}>Er zijn geen openstaande deadlines voor jouw projecten</Typography>
        </Box>
      )}

      {!loading && groupMode === 'date' && visibleItems.length > 0 && (
        <Box>
          {['overdue', 'today', 'thisWeek', 'nextWeek', 'later'].map(gk => (
            <DateGroup key={gk} groupKey={gk} items={itemsByDateGroup[gk]} onItemClick={handleItemClick} />
          ))}
        </Box>
      )}

      {!loading && groupMode === 'project' && Object.keys(visibleByProject).length > 0 && (
        <Box>
          {Object.entries(visibleByProject)
            .sort((a, b) => Math.min(...a[1].items.map(i => i.daysUntil)) - Math.min(...b[1].items.map(i => i.daysUntil)))
            .map(([projectId, project]) => (
              <ProjectGroup key={projectId} project={project} items={project.items} expanded={expandedProjects[projectId] ?? true} onToggle={() => toggleProject(projectId)} onItemClick={handleItemClick} />
            ))}
        </Box>
      )}
    </Box>
  );
}

export default DeadlineOverview;
